/**
 * ClickReel Analytics Bridge
 *
 * Detects host page GA4 (gtag) and Meta Pixel (fbq) and fires ClickReel
 * engagement events through them. This enables native conversion path
 * attribution — the events share the same cookies/client IDs as purchase
 * events, so GA4 and Meta can correlate video views with purchases.
 *
 * Runs alongside (not replacing) the existing server-side sync.
 */

import { logger } from './logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AnalyticsBridgeConfig {
  ga4: boolean;
  metaPixel: boolean;
}

interface BridgeContext {
  campaignId: string | undefined;
  campaignName: string | undefined;
  sessionId: string | undefined;
}

// Declare global analytics functions
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let bridgeConfig: AnalyticsBridgeConfig = { ga4: true, metaPixel: true };
let bridgeContext: BridgeContext = {
  campaignId: undefined,
  campaignName: undefined,
  sessionId: undefined,
};
let ga4Available = false;
let metaPixelAvailable = false;

// Track which video_progress quartiles we've already fired to GA4/Meta
// to avoid duplicate events (key: "videoId:quartile")
const firedProgressQuartiles = new Set<string>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize the analytics bridge. Call once after SDK init.
 */
export function initAnalyticsBridge(
  config: { ga4?: boolean; metaPixel?: boolean } | undefined,
  context: BridgeContext
): void {
  bridgeConfig = {
    ga4: config?.ga4 !== false,
    metaPixel: config?.metaPixel !== false,
  };
  bridgeContext = context;

  ga4Available = bridgeConfig.ga4 && typeof window.gtag === 'function';
  metaPixelAvailable = bridgeConfig.metaPixel && typeof window.fbq === 'function';

  if (ga4Available) {
    logger.debug('Analytics bridge: GA4 (gtag) detected');
  }
  if (metaPixelAvailable) {
    logger.debug('Analytics bridge: Meta Pixel (fbq) detected');
  }
  if (!ga4Available && !metaPixelAvailable) {
    logger.debug('Analytics bridge: no host analytics detected, bridge inactive');
  }
}

/**
 * Update bridge context (e.g. when session ID arrives from API).
 */
export function updateBridgeContext(partial: Partial<BridgeContext>): void {
  bridgeContext = { ...bridgeContext, ...partial };
}

/**
 * Fire analytics bridge events for a given SDK event type.
 * Called from trackEvent() in index.ts.
 */
export function fireAnalyticsBridgeEvent(
  eventType: string,
  data: Record<string, unknown>
): void {
  if (!ga4Available && !metaPixelAvailable) return;

  const baseParams = {
    cr_campaign_id: bridgeContext.campaignId || '',
    cr_campaign_name: bridgeContext.campaignName || '',
    cr_video_id: (data.videoId as string) || '',
    cr_session_id: bridgeContext.sessionId || '',
  };

  switch (eventType) {
    case 'video_play':
      fireGA4('clickreel_video_view', {
        ...baseParams,
        video_source: data.source || '',
      });
      fireMeta('track', 'ViewContent', {
        content_type: 'clickreel_video',
        content_name: 'VideoStart',
        ...baseParams,
      });
      break;

    case 'video_progress': {
      const quartile = data.quartile as number;
      if (quartile > 0) {
        const key = `${data.videoId}:${quartile}`;
        if (firedProgressQuartiles.has(key)) break;
        firedProgressQuartiles.add(key);

        fireGA4('clickreel_video_progress', {
          ...baseParams,
          video_percent: quartile,
          video_current_time: data.currentTime || 0,
          video_duration: data.duration || 0,
        });
        // Meta: only fire ViewContent at 50% and 100% to avoid noise
        if (quartile >= 50) {
          fireMeta('track', 'ViewContent', {
            content_type: 'clickreel_video',
            content_name: `VideoWatch${quartile}`,
            ...baseParams,
          });
        }
      }
      break;
    }

    case 'video_complete':
      fireGA4('clickreel_video_complete', baseParams);
      fireMeta('track', 'ViewContent', {
        content_type: 'clickreel_video',
        content_name: 'VideoComplete',
        ...baseParams,
      });
      break;

    case 'cta_click':
      fireGA4('clickreel_cta_click', {
        ...baseParams,
        destination_url: data.ctaUrl || '',
      });
      fireMeta('track', 'Lead', {
        content_type: 'clickreel_cta',
        ...baseParams,
      });
      break;

    case 'dismiss':
      fireGA4('clickreel_dismiss', baseParams);
      break;

    case 'video_close':
      fireGA4('clickreel_video_close', baseParams);
      break;

    // story events
    case 'story_start':
      fireGA4('clickreel_story_start', baseParams);
      break;

    case 'story_complete':
      fireGA4('clickreel_story_complete', baseParams);
      break;

    default:
      // Other events are not bridged to host analytics
      break;
  }
}

/**
 * Build a CTA URL with UTM attribution parameters.
 * Used by player files to append tracking to CTA links.
 */
export function buildCtaUrl(
  originalUrl: string | undefined,
  context: {
    campaignName?: string;
    videoId?: string;
    sessionId?: string;
  }
): string {
  if (!originalUrl || originalUrl === '#') return originalUrl || '#';

  try {
    const url = new URL(originalUrl, window.location.origin);

    // Don't overwrite existing UTM params
    if (!url.searchParams.has('utm_source')) {
      url.searchParams.set('utm_source', 'clickreel');
    }
    if (!url.searchParams.has('utm_medium')) {
      url.searchParams.set('utm_medium', 'video');
    }
    if (!url.searchParams.has('utm_campaign') && context.campaignName) {
      url.searchParams.set('utm_campaign', context.campaignName);
    }
    if (!url.searchParams.has('utm_content') && context.videoId) {
      url.searchParams.set('utm_content', context.videoId);
    }
    if (context.sessionId) {
      url.searchParams.set('cr_session', context.sessionId);
    }

    return url.toString();
  } catch {
    // If URL parsing fails, return original
    logger.debug('Analytics bridge: could not parse CTA URL, returning original');
    return originalUrl;
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function fireGA4(eventName: string, params: Record<string, unknown>): void {
  if (!ga4Available || !window.gtag) return;
  try {
    window.gtag('event', eventName, params);
    logger.debug(`Analytics bridge GA4: ${eventName}`, params);
  } catch (e) {
    logger.debug('Analytics bridge GA4 error:', e);
  }
}

function fireMeta(
  method: 'track' | 'trackCustom',
  eventName: string,
  params: Record<string, unknown>
): void {
  if (!metaPixelAvailable || !window.fbq) return;
  try {
    window.fbq(method, eventName, params);
    logger.debug(`Analytics bridge Meta: ${method} ${eventName}`, params);
  } catch (e) {
    logger.debug('Analytics bridge Meta error:', e);
  }
}
